import { Button, DataTable, PageHead } from "@nabble/ui";
import { useMemo, useState } from "react";
import { fetchPermissions, type Permission } from "../../../api/adminPermissions";
import { fetchRoles, type Role } from "../../../api/adminRoles";
import { useLoadedData } from "../../../hooks/useLoadedData";
import { DeleteRoleDialog } from "./DeleteRoleDialog";
import { RoleDialog } from "./RoleDialog";
import { roleColumns } from "./roleColumns";

interface RolesData {
  roles: Role[];
  permissions: Permission[];
}

const NOTHING_LOADED: RolesData = { roles: [], permissions: [] };

async function loadRoles(): Promise<RolesData> {
  const [roles, permissions] = await Promise.all([fetchRoles(), fetchPermissions()]);
  return { roles, permissions };
}

export default function AdminRolesPage(): React.JSX.Element {
  const { data, loading, loadError, reload } = useLoadedData(loadRoles, "We couldn't load the roles.");
  const { roles, permissions } = data ?? NOTHING_LOADED;
  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const columns = useMemo(() => roleColumns({ open: setEditing, remove: setDeleteTarget }), []);

  return (
    <div>
      <PageHead titleHidden title="Roles" actions={<Button label="Create role" icon="plus" onClick={() => setEditing("new")} />} />

      <DataTable<Role>
        id="admin-roles"
        rowKey="id"
        loading={loading}
        rows={roles}
        columns={columns}
        exportName="roles"
        searchPlaceholder="Search roles"
        defaultSort={{ key: "name", dir: "asc" }}
        onRowClick={(r) => setEditing(r)}
        error={loadError ? { title: "We couldn't load the roles", description: loadError, action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} /> } : null}
        empty={{
          icon: "key",
          title: "No roles defined",
          description: "Create a role for a kind of work (for example Accounting) and give it to people in each organization.",
          action: <Button label="Create role" icon="plus" onClick={() => setEditing("new")} />,
        }}
      />

      {editing ? (
        <RoleDialog
          role={editing === "new" ? null : editing}
          permissions={permissions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      <DeleteRoleDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          reload();
        }}
      />
    </div>
  );
}
